import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuthenticatedFetch } from '../utils/api';
import useOptimizationType from './useOptimizationType';
import { showErrorToast } from '../utils/toast';

// ─── SessionStorage persistence ───────────────────────────────────────────────

const TC_KEY = 'tc_session_v1';

function loadTcSession() {
  try { return JSON.parse(sessionStorage.getItem(TC_KEY) || 'null'); }
  catch { return null; }
}
function saveTcSession(data) {
  try { sessionStorage.setItem(TC_KEY, JSON.stringify(data)); }
  catch { /* quota exceeded — silent */ }
}
function clearTcSession() {
  try { sessionStorage.removeItem(TC_KEY); } catch {}
}

// Map → plain array for JSON, filtering out 'processing' (those were interrupted mid-session)
function serializeStatuses(map) {
  return Array.from(map.entries()).filter(([, s]) => s?.status !== 'processing');
}
function deserializeStatuses(arr) {
  const map = new Map();
  for (const [id, s] of (arr || [])) map.set(id, s);
  return map;
}

// Strip images to minimal fields so sessionStorage stays small
function stripImages(images) {
  return images.map(img => ({
    id: img.id, url: img.url, filename: img.filename,
    shopifyFileId: img.shopifyFileId,
    shopifyRef: img.shopifyRef, themeFile: img.themeFile,
    allSourceFiles: img.allSourceFiles, sourcePath: img.sourcePath,
    groupKey: img.groupKey, groupLabel: img.groupLabel, groupType: img.groupType,
    alreadyOptimized: img.alreadyOptimized, notInFilesLibrary: img.notInFilesLibrary,
    jsonPath: img.jsonPath, sectionType: img.sectionType, blockType: img.blockType,
    settingKey: img.settingKey, alt: img.alt, mimeType: img.mimeType,
    originalSize: img.originalSize,
  }));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true if an image can be selected for optimization.
 * Mirrors the isDisabled logic in ThemeImageRow exactly.
 * Exported so ThemeGroup / ThemeCrushPage can reuse it without re-defining it.
 */
export function isImageSelectable(img, imageStatuses) {
  if (img.notInFilesLibrary || !img.url) return false;
  const imgStatus = imageStatuses.get(img.id);
  const rowStatus = imgStatus?.status || (img.alreadyOptimized ? 'already_optimized' : 'waiting');
  return !['processing', 'queued', 'reverted', 'already_optimized', 'optimized', 'error'].includes(rowStatus);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Encapsulates all state and business logic for ThemeCrushPage:
 *  - Theme list fetching & selection
 *  - Scan
 *  - Image selection (individual, group-all, global-all)
 *  - Optimize batch / single group
 *  - Revert
 *  - Accordion expand/collapse
 *  - Derived stats
 */
export function useThemeCrush() {
  const { authenticatedFetch } = useAuthenticatedFetch();

  // ── Rehydrate full session on mount ─────────────────────────────────────
  const [saved] = useState(() => loadTcSession());

  // ── State ────────────────────────────────────────────────────────────────

  const [themes, setThemes]               = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);
  const [selectedThemeId, setSelectedThemeId] = useState(saved?.selectedThemeId || '');

  const [scanning, setScanning]   = useState(false);
  const [scanDone, setScanDone]   = useState(saved?.scanDone || false);
  const [scanError, setScanError] = useState(null);
  const [images, setImages]       = useState(saved?.images || []);
  const [scanMeta, setScanMeta]   = useState(saved?.scanMeta || { skippedCount: 0 });

  const [selectedImages, setSelectedImages]   = useState(new Set());
  const [expandedGroups, setExpandedGroups]   = useState(() => {
    const savedImgs = saved?.images;
    if (saved?.scanDone && savedImgs?.length) {
      return new Set(savedImgs.map(img => img.groupKey || img.themeFile || 'other'));
    }
    return new Set();
  });
  const [optimizing, setOptimizing]           = useState(false);
  const [imageStatuses, setImageStatuses]     = useState(() => deserializeStatuses(saved?.imageStatuses));

  const { optimizationType, handleOptimizationTypeChange } = useOptimizationType('regular');
  const [recentsKey, setRecentsKey] = useState(0);
  const scanAbortRef = useRef(null);

  // ── Persist session ONLY while actively optimizing; clear it at all other times ──
  // This ensures that on page reload or tab switch after completion, no stale state is shown.
  useEffect(() => {
    if (optimizing) {
      saveTcSession({
        selectedThemeId,
        scanDone,
        images: scanDone ? stripImages(images) : [],
        scanMeta: scanDone ? scanMeta : { skippedCount: 0 },
        imageStatuses: serializeStatuses(imageStatuses),
      });
    } else {
      clearTcSession();
    }
  }, [optimizing, selectedThemeId, scanDone, images, scanMeta, imageStatuses]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const themeDbId = useMemo(() => selectedThemeId.split('/').pop(), [selectedThemeId]);

  const groups = useMemo(() => {
    const map = new Map();
    // Exclude already-optimized and unresolvable (notInFilesLibrary) images from the visible list
    images.filter(img => !img.alreadyOptimized && !img.notInFilesLibrary).forEach(img => {
      const key = img.groupKey || img.themeFile || 'other';
      if (!map.has(key)) {
        map.set(key, { key, label: img.groupLabel || key, type: img.groupType || 'other', images: [] });
      }
      map.get(key).images.push(img);
    });
    const typeOrder = { global: 0, template: 1, section: 2, layout: 3, other: 4 };
    return Array.from(map.values()).sort((a, b) => (typeOrder[a.type] ?? 4) - (typeOrder[b.type] ?? 4));
  }, [images]);

  const stats = useMemo(() => {
    const statList        = Array.from(imageStatuses.values());
    const optimizedList   = statList.filter(s => s.status === 'optimized');
    const totalSavedKb    = optimizedList.reduce((sum, s) => {
      const orig = s.result?.originalSize ?? 0;
      const opt  = s.result?.optimizedSize ?? 0;
      return sum + Math.max(0, orig - opt);
    }, 0);
    const alreadyOptimizedCount = images.filter(img => img.alreadyOptimized && !imageStatuses.has(img.id)).length;
    const missingLibraryCount   = images.filter(img => img.notInFilesLibrary).length;
    return {
      total:            images.length,
      selected:         selectedImages.size,
      optimized:        optimizedList.length,
      skipped:          statList.filter(s => s.status === 'skipped').length,
      errors:           statList.filter(s => s.status === 'error').length,
      reverted:         statList.filter(s => s.status === 'reverted').length,
      alreadyOptimized: alreadyOptimizedCount,
      missingLibrary:   missingLibraryCount,
      totalSavedKb,
    };
  }, [images, selectedImages, imageStatuses]);

  // ── Effects ──────────────────────────────────────────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchThemes(); }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const fetchThemes = async () => {
    setThemesLoading(true);
    try {
      const resp = await authenticatedFetch('/api/themes');
      if (resp.ok) {
        const data = await resp.json();
        setThemes(data);
        // Preserve a restored selection if it still exists; otherwise default to first in list
        setSelectedThemeId(prev => {
          if (prev && data.some(t => t.id === prev)) return prev;
          return data[0] ? data[0].id : prev;
        });
      } else {
        const err = await resp.json();
        showErrorToast(err.error || 'Failed to fetch themes');
      }
    } catch { showErrorToast('Failed to fetch themes'); }
    finally { setThemesLoading(false); }
  };

  const handleScanTheme = async () => {
    // A scan is already running: do nothing (use "Stop Scanning" to cancel it first)
    if (!themeDbId || scanAbortRef.current) return;
    const controller = new AbortController();
    scanAbortRef.current = controller;

    setScanning(true);
    setScanDone(false);
    setScanError(null);
    setImages([]);
    setSelectedImages(new Set());
    setExpandedGroups(new Set());
    setImageStatuses(new Map());
    setRecentsKey(k => k + 1);
    try {
      const resp = await authenticatedFetch(`/api/themes/${themeDbId}/scan`, {
        method: 'POST',
        signal: controller.signal,
      });
      if (resp.ok) {
        const payload = await resp.json();
        const imgs = Array.isArray(payload) ? payload : (payload.images || []);
        const meta = Array.isArray(payload) ? { skippedCount: 0 } : { skippedCount: payload.skippedCount || 0 };
        setImages(imgs);
        setScanMeta(meta);
        setExpandedGroups(new Set(imgs.map(img => img.groupKey || img.themeFile || 'other')));
        setScanDone(true);
      } else {
        const err = await resp.json().catch(() => ({}));
        setScanError(err.error || `Failed to scan theme (HTTP ${resp.status}${resp.status === 504 ? ' Gateway Time-out: the proxy in front of our server stopped waiting; this is not a Shopify error.' : ''})`);
      }
    } catch (e) {
      if (e.name !== 'AbortError') setScanError('Failed to scan theme — please check your connection and try again.');
      // AbortError = user cancelled; silently clean up
    } finally {
      scanAbortRef.current = null;
      setScanning(false);
    }
  };

  const handleAbortScan = useCallback(() => {
    if (scanAbortRef.current) {
      scanAbortRef.current.abort();
      scanAbortRef.current = null;
    }
    setScanning(false);
  }, []);

  const handleOptimizeBatch = useCallback(async (toOptimize) => {
    if (toOptimize.length === 0 || optimizing) return;
    setOptimizing(true);
    setSelectedImages(new Set()); // uncheck everything immediately when processing starts
    // Pre-mark all batch images as queued so the UI shows them waiting in line
    setImageStatuses(prev => {
      const next = new Map(prev);
      toOptimize.forEach(img => next.set(img.id, { status: 'queued' }));
      return next;
    });
    for (const img of toOptimize) {
      setImageStatuses(prev => new Map(prev).set(img.id, { status: 'processing' }));
      try {
        const resp = await authenticatedFetch(`/api/themes/${themeDbId}/optimize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ images: [img], optimizationType }),
        });
        if (resp.ok) {
          const [result] = await resp.json();
          const msg = result.message || (
            result.status === 'skipped' ? 'Image already optimally compressed — no size reduction possible' :
            result.status === 'error'   ? 'Image could not be optimized — it may be corrupted or unsupported' : null
          );
          setImageStatuses(prev => new Map(prev).set(img.id, { status: result.status, result, dbId: result.dbId, message: msg }));
        } else {
          const err = await resp.json().catch(() => ({}));
          const msg = err.error || err.details || err.message || 'Server error — please try again';
          setImageStatuses(prev => new Map(prev).set(img.id, { status: 'error', message: msg }));
        }
      } catch (e) {
        setImageStatuses(prev => new Map(prev).set(img.id, { status: 'error', message: e.message || 'Failed to optimize image — please try again' }));
      }
    }
    clearTcSession(); // clear immediately — effect also clears once optimizing goes false
    setOptimizing(false);
    setSelectedImages(new Set()); // clear selection so counts reset cleanly
    setRecentsKey(k => k + 1);
  }, [optimizing, themeDbId, optimizationType, authenticatedFetch]);

  const handleOptimize = useCallback(async () => {
    const toOptimize = images.filter(img => selectedImages.has(img.id));
    await handleOptimizeBatch(toOptimize);
  }, [images, selectedImages, handleOptimizeBatch]);

  const handleRevert = useCallback(async (img, dbId) => {
    setImageStatuses(prev => new Map(prev).set(img.id, { ...(prev.get(img.id) || {}), reverting: true }));
    try {
      // Always use bulk endpoint — groups by source_file + parallel writes, faster even for 1 image
      const resp = await authenticatedFetch(`/api/themes/${themeDbId}/revert-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageIds: [dbId] }),
      });
      if (resp.ok) {
        const { results } = await resp.json();
        const result = results?.[0];
        if (result?.status === 'reverted') {
          setImageStatuses(prev => new Map(prev).set(img.id, { status: 'reverted' }));
        } else {
          const msg = result?.reason || 'Revert failed';
          showErrorToast(msg);
          setImageStatuses(prev => new Map(prev).set(img.id, { ...(prev.get(img.id) || {}), reverting: false }));
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        showErrorToast(err.error || 'Revert failed');
        setImageStatuses(prev => new Map(prev).set(img.id, { ...(prev.get(img.id) || {}), reverting: false }));
      }
    } catch {
      showErrorToast('Revert failed');
      setImageStatuses(prev => new Map(prev).set(img.id, { ...(prev.get(img.id) || {}), reverting: false }));
    }
  }, [themeDbId, authenticatedFetch]);

  const handleToggleOne = useCallback((imgId) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(imgId)) next.delete(imgId); else next.add(imgId);
      return next;
    });
  }, []);

  const handleToggleGroupAll = useCallback((groupImages, forceSelect) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      groupImages.forEach(img => {
        if (!isImageSelectable(img, imageStatuses)) return;
        if (forceSelect) next.add(img.id); else next.delete(img.id);
      });
      return next;
    });
  }, [imageStatuses]);

  const handleToggleAllGlobal = useCallback(() => {
    const selectable = images.filter(img => isImageSelectable(img, imageStatuses));
    if (selectedImages.size === selectable.length && selectable.length > 0) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(selectable.map(img => img.id)));
    }
  }, [images, imageStatuses, selectedImages]);

  const handleToggleExpand = useCallback((key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleThemeSelect = useCallback((themeId) => {
    setSelectedThemeId(themeId);
    setScanDone(false);
    setScanError(null);
    setImages([]);
    setScanMeta({ skippedCount: 0 });
    setImageStatuses(new Map());
    setSelectedImages(new Set());
    clearTcSession();
  }, []);

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    // Data
    themes, themesLoading, selectedThemeId, themeDbId,
    scanning, scanDone, scanError, images, groups, stats, scanMeta,
    selectedImages, expandedGroups, optimizing, imageStatuses,
    optimizationType, handleOptimizationTypeChange, recentsKey,
    // Handlers
    handleScanTheme, handleAbortScan, handleOptimizeBatch, handleOptimize, handleRevert,
    handleToggleOne, handleToggleGroupAll, handleToggleAllGlobal,
    handleToggleExpand, handleThemeSelect,
    // Session
    clearSession: clearTcSession,
  };
}
